import type { Zone } from '@cocoonspace/sdk-js/types/zone'
import { useMediaQuery } from '@cocoonspace/shared/hooks/use-media-query.hook'
import { AnimatePresence } from 'framer-motion'
import type { NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import {
	ContentWrapper,
	FiltersWrapper,
	ListWrapper,
	MapWrapper,
	PageWrapper,
} from '~/components/layouts/search-layout'
import { SeoTags } from '~/components/seo-tags'
import {
	SearchProvider,
	useSearchSpaces,
} from '~/domains/search/hooks/use-search-spaces.hook'
import { useExperiment } from '~/hooks/use-experiment'
import { cn } from '~/lib/utils'
import { twTheme } from '~/theme'
import { NoSpacesFound } from '../no-spaces-found'
import { SpaceCard } from '../space-card'
import { ResetSearchButton } from './reset-search-button'
import { SpacesGroup } from './spaces-group'

const SpaceSearchFilters = dynamic(
	() =>
		import('~/domains/search/components/filters/space-search-filters').then(
			({ SpaceSearchFilters }) => SpaceSearchFilters,
		),
	{ ssr: false },
)

const SearchTour = dynamic(
	() => import('../search-tour').then((mod) => mod.SearchTour),
	{ ssr: false },
)

const SearchMap = dynamic(() => import('../search-map'), {
	ssr: false,
})

const SpacesListSkeleton = () => {
	const { isMapVisible } = useSearchSpaces()

	return (
		<div
			data-testid='spaces-layout-group'
			className={cn(
				'grid grid-cols-1 gap-4 pt-[2px]',
				isMapVisible
					? 'lg:grid-cols-2'
					: 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
			)}
		>
			{Array.from({ length: 6 }).map((_, i) => (
				<div
					key={i}
					className='mb-4'
				>
					<SpaceCard.skeleton />
				</div>
			))}
		</div>
	)
}

export const SearchPageContent: NextPage = () => {
	const router = useRouter()
	const { t } = useTranslation()
	const isOnboardingActive = useExperiment('onboarding')
	const isDesktop = useMediaQuery(`(min-width: ${twTheme.theme.screens.md})`)

	const {
		isMapVisible,
		filters,
		activeAlternative,
		spacesListQuery,
		hasAlternatives,
		hasNoResults,
		setIsMapVisible,
	} = useSearchSpaces()

	// hide the map if loaded from mobile
	useEffect(() => {
		if (router.isReady && !isDesktop) {
			setIsMapVisible(false)
		}
	}, [router.isReady])

	return (
		<PageWrapper>
			<SeoTags
				title={t('search:seoTitle', { zone: router.query.zone as Zone })}
				description={t(`cwCommon:seo.metaDescription.${router.query.zone}`)}
			/>

			<FiltersWrapper>
				<SpaceSearchFilters />
			</FiltersWrapper>

			<ContentWrapper>
				<ListWrapper isMapVisible={isMapVisible}>
					{spacesListQuery.isLoading && <SpacesListSkeleton />}

					{hasNoResults && <NoSpacesFound hasAlternatives={hasAlternatives} />}

					<SpacesGroup
						items={spacesListQuery.data?.data || []}
						timeSlot={{
							day: filters.day,
							start: filters.start,
							end: filters.end,
						}}
					/>

					{spacesListQuery.data?.meta?.alternatives?.map((alternative) => (
						<SpacesGroup
							key={`alternative-${alternative.label}`}
							isAlternative
							label={alternative.label}
							items={alternative.data || []}
							timeSlot={{
								day: alternative.day as any,
								start: alternative.start,
								end: alternative.end,
							}}
						/>
					))}
				</ListWrapper>

				<MapWrapper isMapVisible={isMapVisible}>
					{router.isReady && !!router.query.zone && (
						<>
							<AnimatePresence>
								{activeAlternative && isMapVisible && <ResetSearchButton />}
							</AnimatePresence>

							<SearchMap />
						</>
					)}
				</MapWrapper>

				{isOnboardingActive && <SearchTour />}
			</ContentWrapper>
		</PageWrapper>
	)
}

export const SearchPage: NextPage<{ spaceType?: 'desk' | 'cocoon' }> = ({
	spaceType,
	...props
}) => {
	const router = useRouter()

	return (
		<SearchProvider
			spaceType={router.route === '/desks/[zone]' ? 'desk' : 'cocoon'}
		>
			<SearchPageContent {...props} />
		</SearchProvider>
	)
}
